import { mustExist } from '@getstep/sdk/dist/util/Assert'
import type { FunctionComponent } from 'react'
import { lazy, Suspense } from 'react'

import styles from './fieldset.module.scss'
import { legendId } from '../forms/legend'
import type { FormProps } from '../forms/props'
import { SelfFetchingRichText } from '../rich-text'
import { SectionFeature } from '../section-of-content/features'
import { fragmentToVisualProps } from '../section-of-content/utils'
import { Section } from '@components/layout/section'
import { SectionBlock } from '@components/layout/section-block'
import { SectionVisual } from '@components/layout/section-visual'
import { MarkdownRenderer } from '@components/markdown-renderer'
import type { SectionFragment } from '@graphql/fragments/SectionFragment.graphql'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { EMPTY_SECTION } from '@graphql/placeholders'

/**
 * This component wraps up a bunch of fields and/or submit button.
 */
const FieldsetInternal: FunctionComponent<Props> = ({
    customId,
    next,
    renderSubmit,
    loading,
    section,
    meta,
    renderInsideGrid = true,
}) => {
    const Component = mustExist(
        FIELDSETS[customId as keyof typeof FIELDSETS],
        `Fieldset ${customId} component not found`,
    )

    const { title, subtitle, image, content, feature } =
        section ?? EMPTY_SECTION

    const formComponent = (
        <Suspense fallback={null}>
            <Component
                next={next!}
                renderSubmit={renderSubmit}
                title={title!}
                subtitle={
                    content ? (
                        <SelfFetchingRichText {...content} />
                    ) : (
                        subtitle && <MarkdownRenderer source={subtitle} />
                    )
                }
                meta={meta}
            />
        </Suspense>
    )

    if (!renderInsideGrid) {
        return formComponent
    }

    return (
        <Section
            grid
            loading={loading}
            className={styles.fieldset}
            tag='div'
            role='group'
            aria-labelledby={legendId(customId)}
        >
            <SectionBlock
                align='start'
                justify='start'
                className={styles.fields}
            >
                <div className={styles.fieldbox}>{formComponent}</div>
            </SectionBlock>

            {section && (
                <SectionBlock className={styles.visual} justify='center'>
                    <SectionFeature
                        feature={feature}
                        fallback={
                            <SectionVisual
                                image={image}
                                {...fragmentToVisualProps(section)}
                            />
                        }
                    />
                </SectionBlock>
            )}
        </Section>
    )
}

const FIELDSETS: Partial<
    Record<CustomFieldsetId, FunctionComponent<FormProps<any>>>
> = {
    [CustomFieldsetId.LoginEnterPhone]: lazy(() =>
        import('../forms/enter-phone').then((module) => ({
            default: module.EnterPhone,
        })),
    ),
    [CustomFieldsetId.LoginEnterEmail]: lazy(() =>
        import('../forms/enter-email').then((module) => ({
            default: module.EnterEmail,
        })),
    ),
    [CustomFieldsetId.LoginEnterEmailCode]: lazy(() =>
        import('../forms/enter-code/enter-email-code').then((module) => ({
            default: module.EnterEmailCode,
        })),
    ),
    [CustomFieldsetId.LoginEnterCode]: lazy(() =>
        import('../forms/enter-code/enter-phone-code').then((module) => ({
            default: module.EnterPhoneCode,
        })),
    ),
    [CustomFieldsetId.LoginChooseSso]: lazy(() =>
        import('../forms/choose-sso').then((module) => ({
            default: module.ChooseSSO,
        })),
    ),
    [CustomFieldsetId.KycEnterAddress]: lazy(() =>
        import('../forms/identity/enter-address').then((module) => ({
            default: module.EnterAddress,
        })),
    ),
    [CustomFieldsetId.KycEnterLastFourSsn]: lazy(() =>
        import('../forms/identity/enter-last-four-ssn').then((module) => ({
            default: module.EnterLastFourSsn,
        })),
    ),
    [CustomFieldsetId.KycEnterFullSsn]: lazy(() =>
        import('../forms/identity/enter-full-ssn').then((module) => ({
            default: module.EnterFullSsn,
        })),
    ),
    [CustomFieldsetId.KycEnterPersonalInfo]: lazy(() =>
        import('../forms/identity/enter-personal-info').then((module) => ({
            default: module.EnterPersonalInfo,
        })),
    ),
    [CustomFieldsetId.SchoolRequestWaitlist]: lazy(() =>
        import('../forms/school-request-waitlist').then((module) => ({
            default: module.SchoolRequestWaitlist,
        })),
    ),
    [CustomFieldsetId.StepBlackWaitlist]: lazy(() =>
        import('../forms/step-black-waitlist').then((module) => ({
            default: module.StepBlackWaitlist,
        })),
    ),
    [CustomFieldsetId.SavingsCalculator]: lazy(() =>
        import('../forms/savings-calculator').then((module) => ({
            default: module.SavingsCalculatorForm,
        })),
    ),
}

type Props = Pick<FormProps<any>, 'renderSubmit' | 'next'> & {
    customId: CustomFieldsetId
    preset?: 'primary' | 'default'
    section?: SectionFragment
    loading?: boolean
    renderInsideGrid?: boolean
    meta?: JSON
}

export default FieldsetInternal
